import Header from "@/atomic/atoms/Header";
import { PropsView } from "../Models/creditCard";
import Spinner from "@/atomic/atoms/Spinner";
import LoadingOverlay from "@/atomic/atoms/LoadingOverlay";
import { InputMask } from "@react-input/mask";
import Modal from "@/atomic/atoms/Modal";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";
import { useState, useEffect } from "react";

const INPUT_CLASS =
  "w-full text-base text-text bg-transparent border border-[#bfbfbf] p-4 rounded-[12px] h-14 placeholder:text-text-secondary tracking-[-0.4px] outline-none focus:border-primary transition-colors";

export default function RechargeCreditCardView({
  isLoading,
  goBack,
  isDisabled,
  error,
  errorChangePaymentMethod,
  goToFinished,
  register,
  handleSubmit,
  errors,
  isVisibleModal,
  toggleModal,
  isVisibleConfirmationModal,
  handleConfirmAutoPayment,
  handleCancelAutoPayment,
  handleAutoPaymentChange,
  customer,
  watch,
  isAutoPaymentEnabled = true,
  attemptCount,
  resetAttemptCount,
  goPaymentOptions,
  captchaName = "mtcaptcha-recharge-credit-card-0",
  isCaptchaVerified = false,
  isLoadingCaptchaFlag = false,
}: PropsView) {
  const { t } = useI18n();
  const { themeName } = useThemeContext();
  const title = themeName === "zapay" ? "Pedágio" : t("pages:checkout:payment");

  const saveValue = watch("save");
  const [showCaptcha, setShowCaptcha] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCaptcha(true);
    }, 100);

    return () => {
      clearTimeout(timer);
      setShowCaptcha(false);
    };
  }, [captchaName]);

  return (
    <div className="h-full flex flex-col">
      <Header title={title} hasLeftButton goBack={goBack} />

      <form
        className="flex-1 flex flex-col overflow-hidden bg-white"
        onSubmit={handleSubmit}
      >
        <main className="flex-1 overflow-y-auto px-4 pb-28">
          <div className="max-w-[480px] mx-auto">
            <div className="flex flex-col gap-[17px]">
              <h2 className="text-[22px] font-bold text-text mt-6 tracking-[-0.2px] leading-[1.2]">
                Informe os dados do cartão
              </h2>

              <div className="bg-[#e6e6e6] rounded-2xl h-[100px] w-full relative flex items-center overflow-hidden">
                <div className="flex flex-col gap-1 pl-5 pr-2 z-10 max-w-[55%]">
                  <p className="text-sm font-medium text-text tracking-[-0.4px] leading-[1.3]">
                    Cartão Sem Parar Mais
                  </p>
                  <p className="text-xs text-text-secondary leading-[1.4]">
                    Economize até R$ 35,00 ao parcelar
                  </p>
                  <a
                    href="https://www.semparar.com.br/sem-parar-mais"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-primary tracking-[-0.4px] leading-[1.3] mt-1"
                  >
                    Pedir agora
                  </a>
                </div>
                <img
                  src="/svg/card-semparar.svg"
                  alt="Cartão Sem Parar"
                  className="absolute right-[-16px] -top-3 h-[140px] rotate-[16deg] pointer-events-none drop-shadow-md"
                />
              </div>

              <div className="w-full">
                <InputMask
                  {...register("number")}
                  name="number"
                  required
                  type="text"
                  inputMode="numeric"
                  mask="____ ____ ____ ______"
                  replacement={{ _: /\d/ }}
                  placeholder="Número do cartão de crédito"
                  className={INPUT_CLASS}
                />
                <div className="min-h-[1rem] mt-1">
                  {errors.number && (
                    <span className="text-error text-xs break-words">
                      {errors.number.message as string}
                    </span>
                  )}
                </div>
              </div>

              <div className="w-full">
                <input
                  {...register("name")}
                  required
                  type="text"
                  name="name"
                  placeholder="Nome impresso no cartão"
                  className={INPUT_CLASS}
                />
                <div className="min-h-[1rem] mt-1">
                  {errors.name && (
                    <span className="text-error text-xs break-words">
                      {errors.name.message as string}
                    </span>
                  )}
                </div>
              </div>

              <div className="w-full">
                <InputMask
                  {...register("expiration")}
                  name="expiration"
                  required
                  type="text"
                  inputMode="numeric"
                  mask="__/____"
                  replacement={{ _: /\d/ }}
                  placeholder="Validade"
                  className={INPUT_CLASS}
                />
                <div className="min-h-[1rem] mt-1">
                  {errors.expiration && (
                    <span className="text-error text-xs break-words">
                      {errors.expiration.message as string}
                    </span>
                  )}
                </div>
              </div>

              <div
                className={`flex items-center gap-3 p-5 rounded-2xl border ${saveValue ? "bg-background border-primary" : "bg-background border-border"}`}
              >
                <img
                  src="/svg/icon-card-2.0.svg"
                  width={32}
                  height={32}
                  alt=""
                  className="shrink-0"
                />
                <div className="flex-1 flex flex-col">
                  <p className="text-sm font-medium text-text tracking-[-0.4px] leading-[18px]">
                    Salvar cartão
                  </p>
                  <p className="text-sm font-light text-text-secondary tracking-[-0.4px] leading-[18px]">
                    Para pagamentos futuros
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    {...register("save")}
                    type="checkbox"
                    name="save"
                    className="sr-only peer"
                    onChange={handleAutoPaymentChange}
                  />
                  <div className="w-9 h-[22px] bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-[18px] after:w-[18px] after:transition-all peer-checked:after:translate-x-full" />
                </label>
              </div>
            </div>

            {showCaptcha && (
              <div className="w-full mb-4 flex justify-center relative">
                <LoadingOverlay
                  isLoading={isLoadingCaptchaFlag}
                  bgColor="bg-white/50"
                />
                <div
                  className="mtcaptcha"
                  id={captchaName}
                  data-callback="onCaptchaSuccess"
                ></div>
              </div>
            )}
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 px-5 py-2.5 bg-white">
          <div className="max-w-[480px] mx-auto">
            <button
              type="submit"
              disabled={isDisabled || !isCaptchaVerified}
              className={`w-full h-[60px] rounded-theme-large text-base font-medium text-white tracking-[-0.4px] transition-opacity ${
                isDisabled || !isCaptchaVerified
                  ? "bg-disabled text-disabled-text cursor-not-allowed"
                  : "bg-primary cursor-pointer"
              }`}
            >
              {t("common:continue")}
            </button>
          </div>
        </div>
      </form>

      {isLoading && <Spinner />}

      {error && isVisibleModal && attemptCount === 1 && (
        <Modal
          title={t("pages:checkout:errors:chargeFailedTitle")}
          message={t("pages:checkout:errors:chargeFailedMessage")}
          action={() => {
            toggleModal();
            handleSubmit();
          }}
          actionMessage={t("pages:checkout:errors:tryAgain")}
          actionSecond={toggleModal}
          actionSecondMessage={t("common:cancel")}
          actionClose={toggleModal}
        />
      )}

      {error && isVisibleModal && attemptCount >= 2 && (
        <Modal
          title={t("pages:checkout:errors:processingFailedTitle")}
          message={t("pages:checkout:errors:processingFailedMessage")}
          action={() => {
            toggleModal();
            resetAttemptCount();
            goPaymentOptions();
          }}
          actionMessage={t("common:back")}
          actionClose={() => {
            toggleModal();
            resetAttemptCount();
            goPaymentOptions();
          }}
        />
      )}

      {errorChangePaymentMethod && isVisibleModal && (
        <Modal
          title={t("pages:checkout:errors:chargeFailedTitle")}
          message="Conseguimos quitar suas passagens selecionadas mas não foi possível definir seu cartão como método principal de pagamento. Tente defini-lo mais tarde."
          actionMessage="Ok"
          action={() => {
            toggleModal();
            goToFinished();
          }}
        />
      )}

      {isVisibleConfirmationModal && isAutoPaymentEnabled && (
        <Modal
          title="Atenção!"
          messageElement={
            <div className="text-center">
              <p className="text-sm text-text mb-4">
                Todas as passagens selecionadas e as novas serão debitadas no
                cartão informado.
              </p>
              <p className="text-sm text-text mb-4">
                Tem certeza que deseja ativar o pagamento direto no cartão:
              </p>
              <p className="text-sm text-text font-mono">
                **** **** ****{" "}
                {watch("number")?.replace(/\D/g, "").slice(-4) || "0000"}
              </p>
            </div>
          }
          actionMessage="Sim"
          action={handleConfirmAutoPayment}
          actionSecondMessage="Não"
          actionSecond={handleCancelAutoPayment}
        />
      )}
    </div>
  );
}
